import { cn } from '@heidi/utils';
import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

// Two lines of context above the first change when the panel opens on it.
const FIRST_CHANGE_INSET_PX = 40;

const FADES = {
  'background-tertiary': {
    top: 'from-background-tertiary',
    bottom: 'from-background-tertiary',
  },
  surface: { top: 'from-surface', bottom: 'from-surface' },
  'surface-accent': { top: 'from-surface-accent', bottom: 'from-surface-accent' },
} as const;

export type ChangeScrollPanelSurface = keyof typeof FADES;

/**
 * A height-capped body that opens on its first `[data-change]` and fades the
 * edges with more to read. A change can arrive after mount (a draft's tint
 * lands once its text settles), so the panel watches its content for the
 * first one. While `followEnd` is set it stays on the newest line instead;
 * when that ends with no change to show, it returns to the top.
 */
export const ChangeScrollPanel = ({
  children,
  surface,
  className,
  scrollClassName,
  followEnd = false,
  testIdPrefix,
}: {
  children: ReactNode;
  /** The surface behind the panel, which its fades blend into. */
  surface: ChangeScrollPanelSurface;
  className?: string;
  /** Height cap and padding of the scrolling element. */
  scrollClassName: string;
  followEnd?: boolean;
  testIdPrefix: string;
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const openedOnChange = useRef(false);
  const followingEnd = useRef(followEnd);
  const [showFade, setShowFade] = useState(false);
  const [showTopFade, setShowTopFade] = useState(false);

  const updateFade = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      setShowFade(false);
      setShowTopFade(false);
      return;
    }
    const overflowing = el.scrollHeight - el.clientHeight > 1;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
    setShowFade(overflowing && !atBottom);
    setShowTopFade(overflowing && el.scrollTop > 1);
  }, []);

  const place = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (followingEnd.current) {
      el.scrollTop = el.scrollHeight;
      return;
    }
    if (openedOnChange.current) return;
    const firstChange = el.querySelector<HTMLElement>('[data-change]');
    if (!firstChange) return;
    openedOnChange.current = true;
    // Relative to the current scrollTop, so a second run (Strict Mode) is a no-op.
    el.scrollTop = Math.max(
      0,
      el.scrollTop +
        firstChange.getBoundingClientRect().top -
        el.getBoundingClientRect().top -
        FIRST_CHANGE_INSET_PX
    );
  }, []);

  useLayoutEffect(() => {
    const wasFollowing = followingEnd.current;
    followingEnd.current = followEnd;
    const el = scrollRef.current;
    if (wasFollowing && !followEnd && el) el.scrollTop = 0;
    place();
    updateFade();
  }, [followEnd, place, updateFade]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof MutationObserver === 'undefined') return;
    const observer = new MutationObserver(() => {
      place();
      updateFade();
    });
    observer.observe(el, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, [place, updateFade]);

  useLayoutEffect(() => {
    updateFade();
  });

  return (
    <div className={cn('relative overflow-hidden', className)}>
      <div
        ref={scrollRef}
        className={cn('scrollbar-subtle overflow-y-auto', scrollClassName)}
        data-testid={`${testIdPrefix}-panel`}
        onScroll={updateFade}
      >
        {children}
      </div>
      {showTopFade ? (
        <div
          data-testid={`${testIdPrefix}-fade-top`}
          className={cn(
            'pointer-events-none absolute inset-x-0 top-0 h-8 bg-gradient-to-b to-transparent',
            FADES[surface].top
          )}
        />
      ) : null}
      {showFade ? (
        <div
          data-testid={`${testIdPrefix}-fade`}
          className={cn(
            'pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t to-transparent',
            FADES[surface].bottom
          )}
        />
      ) : null}
    </div>
  );
};
